import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowRight,
  FiArrowUpRight,
  FiCheckCircle,
} from 'react-icons/fi';

const CHAIN_LABELS = {
  prc: 'PRC Mainnet',
  solana: 'Solana',
  ethereum: 'Ethereum',
  polygon: 'Polygon',
  tron: 'Tron',
};

function InfoRow({ label, value, muted }) {
  if (!value) return null;
  return (
    <div className="tx-addinfo-row">
      <span className="tx-addinfo-row-label">{label}</span>
      <span className={`tx-addinfo-row-value${muted ? ' tx-addinfo-muted' : ''}`}>{value}</span>
    </div>
  );
}

function TxAdditionalInfoPopover({ anchor, signature, tx, onClose }) {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || !anchor) return undefined;

    const pad = 12;
    const gap = 10;
    el.style.visibility = 'hidden';
    el.classList.remove('tx-addinfo--beak-end');

    const place = () => {
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const centerY = anchor.top + anchor.height / 2;
      let left = anchor.right + gap;
      if (left + width > window.innerWidth - pad) {
        left = anchor.left - gap - width;
        el.classList.add('tx-addinfo--beak-end');
      }
      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
      let top = centerY;
      top = Math.max(pad + height / 2, Math.min(top, window.innerHeight - pad - height / 2));
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.transform = 'translateY(-50%)';
      el.style.visibility = 'visible';
    };

    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, signature]);

  const detailTo = `/tx/${encodeURIComponent(signature)}`;
  const chain = tx?.chain || 'prc';
  const chainLabel = CHAIN_LABELS[chain] || chain.toUpperCase();
  const isSuccess = tx?.success !== false;
  const isSwap = tx?.type === 'swap' || tx?.action === 'Swap';

  return createPortal(
    <div
      ref={rootRef}
      className="tx-addinfo-popover"
      role="dialog"
      aria-label="Transaction details"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="tx-addinfo-head">
        <strong>Transaction Details</strong>
        <Link to={detailTo} className="tx-addinfo-more" onClick={onClose}>
          Full details <FiArrowUpRight />
        </Link>
      </div>

      <div className="tx-addinfo-tags">
        <span className="tx-addinfo-tag tx-addinfo-tag--transfer">
          <FiArrowUpRight className="tx-addinfo-tag-icon" aria-hidden />
          {tx?.action || 'Transaction'}
        </span>
        <span className="tx-addinfo-tag tx-addinfo-tag--plain">{chainLabel}</span>
        {isSuccess ? (
          <span className="tx-addinfo-badge tx-addinfo-badge--success">
            <FiCheckCircle aria-hidden />
            Success
          </span>
        ) : (
          <span className="tx-addinfo-badge tx-addinfo-badge--fail">
            <FiAlertCircle aria-hidden />
            Failed
          </span>
        )}
      </div>

      <div className="tx-addinfo-divider" />

      <div className="tx-addinfo-details">
        <InfoRow label="Amount" value={tx?.amount} />
        <InfoRow label="Transaction Fee" value={tx?.fee} />
        {tx?.feePercent ? <InfoRow label="Fee Rate" value={tx.feePercent} /> : null}
        {tx?.netAmount ? <InfoRow label="Net Amount" value={tx.netAmount} /> : null}

        {isSwap && tx?.usdtValue ? (
          <>
            <div className="tx-addinfo-divider" />
            <div className="tx-addinfo-swap-summary">
              <span className="tx-addinfo-swap-from">{tx.amount}</span>
              <FiArrowRight className="tx-addinfo-swap-arrow" />
              <span className="tx-addinfo-swap-to">{tx.usdtValue}</span>
            </div>
            {tx.prcPrice ? (
              <p className="tx-addinfo-rate">Rate: 1 PRC = {tx.prcPrice} USDT</p>
            ) : null}
          </>
        ) : null}

        <div className="tx-addinfo-divider" />

        <InfoRow label="From" value={tx?.sender} />
        <InfoRow label="To" value={tx?.receiver} />
        <InfoRow label="Block" value={tx?.block} />
        {tx?.time ? <InfoRow label="Time" value={tx.time} /> : null}
      </div>
    </div>,
    document.body,
  );
}

export default TxAdditionalInfoPopover;
